import { Widget } from "@foretagsplatsen/widgetjs";
import spinner from "../../elements/spinner/spinner.js";
import stacker from "../../elements/stacker/stacker.js";

/**
 *  Render a button which the button text is replaced by a spinner when clicking on it.
 *  The spinner should stay visible until the promise returned by the callback is resolved or failed.
 *
 * @param {Renderable} label - Usually a short text to render within the button
 * @param {Renderable} waitingLabel - Usually a short text to render
 *   within the button after it has been clicked and while the promise
 *   returned by `onClick` is pending
 * @param {function} onClick - Callback executed on button click, which should return a promise
 * @param {boolean} [isDisabled=false] - Disables click and mark button as disabled in UI
 * @param {boolean} [isPrimary=false] - Mark button as primary button in UI
 * @param {string} [dataTestName] - A string to be used to find the button with the test framework
 * @param {boolean} [isAnchor=false] - Makes an anchor instead of a button
 * @param {string} [cssClass] - Extra css class for the widget
 */
export default class WaitingButtonWidget extends Widget {
	constructor({
		label,
		waitingLabel = () => {},
		onClick,
		isDisabled = false,
		isPrimary = false,
		dataTestName,
		isAnchor = false,
		cssClass = "",
	}) {
		super(...arguments);

		this._label = label;
		this._waitingLabel = waitingLabel;
		this._onClick = onClick;
		this._isDisabled = isDisabled;
		this._isPrimary = isPrimary;
		this._dataTestName = dataTestName;
		this._isAnchor = isAnchor;
		this._cssClass = cssClass;

		this._loading = false;
	}

	renderContentOn(html) {
		html.a(
			{
				"data-test": this._dataTestName,
				click: () => this._triggerClickEvent(),
				klass: this._getButtonCssClass(),
			},
			this._loading ? this._renderLoadingOn.bind(this) : this._label,
		);
	}

	_renderLoadingOn(html) {
		html.render(stacker(this._waitingLabel, spinner));
	}

	_getButtonCssClass() {
		const typeOfButton =
			this._isPrimary && !this._loading ? "btn-primary" : "btn-default";

		return [
			"WaitingButtonWidget",
			{ btn: !this._isAnchor },
			{ [typeOfButton]: !this._isAnchor },
			{ disabled: this._isDisabled || this._loading },
			this._cssClass,
		];
	}

	_triggerClickEvent() {
		if (this._isDisabled || this._loading) {
			return;
		}

		this._setLoading(true);

		this._onClick().finally(() => {
			this._setLoading(false);
		});
	}

	_setLoading(isloading) {
		this._loading = isloading;
		this.update();
	}
}
